// Derives each tunnel's per-side window counts straight from the plant counts,
// instead of the block-level access_window. GreenAmex standard: 10 plants per
// window (soil) / 21.6 (hydroponic). So windows = round(number_of_plants / ppw).
// The SUB_A/SUB_B label comes from the row_id (already correct after the block_rows
// swaps), so the map faithfully reflects the data with no inversion guessing.

export const PLANTS_PER_WINDOW = { soil: 10, hydro: 21.6 }

// row_id looks like `{block}[-SUB_A|-SUB_B]-T{tunnel}-R{line}` (the sub-block
// token is optional and some ids carry stray spaces). Anchor the tunnel/line at
// the end and match the SUB token as a plain substring.
export const parseTunnelSide = (rowId) => {
  const id = rowId || ''
  const m = /T(\d+)-R\d+\s*$/.exec(id)
  const tunnel = m ? Number(m[1]) : null
  const side = id.includes('SUB_A') ? 'A' : id.includes('SUB_B') ? 'B' : 'FULL'
  return { tunnel, side }
}

// Most common value in a list (ties resolved by the largest), or null when empty.
const representative = (values) => {
  if (values.length === 0) return null
  const counts = new Map()
  for (const v of values) counts.set(v, (counts.get(v) || 0) + 1)
  let best = null
  let bestCount = -1
  for (const [v, c] of counts) {
    if (c > bestCount || (c === bestCount && v > best)) {
      best = v
      bestCount = c
    }
  }
  return best
}

// Build a per-tunnel window map from the block rows.
// Returns { byTunnel: Map<tunnelNumber, { subA, subB, full }>, maxSubA, maxSubB, hasData }.
export const deriveTunnelWindows = (rows, isHydroponic) => {
  const ppw = isHydroponic ? PLANTS_PER_WINDOW.hydro : PLANTS_PER_WINDOW.soil
  // tunnel -> { A: [windows...], B: [...], FULL: [...] }
  const buckets = new Map()

  for (const row of rows || []) {
    const { tunnel, side } = parseTunnelSide(row?.rowId)
    if (tunnel == null) continue
    const plants = Number(row?.numberOfPlants)
    if (!Number.isFinite(plants) || plants <= 0) continue
    const windows = Math.round(plants / ppw)
    if (windows <= 0) continue
    if (!buckets.has(tunnel)) buckets.set(tunnel, { A: [], B: [], FULL: [] })
    buckets.get(tunnel)[side].push(windows)
  }

  const byTunnel = new Map()
  let maxSubA = 0
  let maxSubB = 0
  for (const [tunnel, sides] of buckets) {
    const subA = representative(sides.A) || 0
    const subB = representative(sides.B) || 0
    const full = representative(sides.FULL) || 0
    byTunnel.set(tunnel, { subA, subB, full })
    if (subA > maxSubA) maxSubA = subA
    if (subB > maxSubB) maxSubB = subB
  }

  return { byTunnel, maxSubA, maxSubB, hasData: byTunnel.size > 0 }
}
